import { DateTime } from 'luxon';
import clone from 'lodash/clone';
import cloneDeep from 'lodash/cloneDeep';
import MicromobilityHelper, { StationWithDistance, VehicleWithDistance } from '../../components/TravelPlanner/helpers/micromobility.helper';
import { DateTimeRelatesTo } from '../../models/date-time-relates-to.enum';
import { IJourney } from '../../models/journey.model';
import { IMicromobilityData } from '../../models/labs/micromobility/micromobility-data.model';
import { IPaginationResult } from '../../models/pagination-result.model';
import { TransportMode } from '../../models/transport-mode.enum';
import { TransportSubMode } from '../../models/transport-sub-mode.enum';
import { ApiPlaneraResaService } from '../api-planera-resa/api-planera-resa.service';
import LogService from '../log-service/log.service';
import { MicromobilityDataApiModel } from './models/micromobility-data.api-model';
import { ICoordinate } from '../../models/coordinate.model';
import { JourneyHelper } from '../../helpers/journey.helper';
import { FormFactor } from '../../models/labs/micromobility/form-factor.enum';
import { PropulsionType } from '../../models/labs/micromobility/propulsion-type.enum';
import { IAccessLink } from '../../models/access-link.model';
import { StopAreaHelper } from '../../helpers/stop-area.helper';
import { IMicromobilityFilters } from '../../components/Shared/MicromobilityFilters/micromobility-filters.model';
import { ITripLeg } from '../../models/trip-leg.model';
import { IMicromobilityLink } from '../../models/labs/micromobility/micromobility-link.model';
import { IVehicle } from '../../models/labs/micromobility/vehicle.model';
import { IStation } from '../../models/labs/micromobility/station.model';

const MAX_NUMBER_OF_JOURNEYS = 4;
const MAX_TRIP_LEG_DURATION_IN_MINUTES = 10;
const MAX_WALKING_DISTANCE_IN_METERS = 250;
const MAX_MICROMOBILITY_TRIP_LEG_LENGTH_IN_METERS = 10000;
const INTERCHANGE_DURATION_IN_MINUTES = 3;

const DOCKLESS_VEHICLE_TYPES = [
  { formFactor: FormFactor.Scooter, propulsionType: PropulsionType.Electric },
  { formFactor: FormFactor.Bicycle, propulsionType: PropulsionType.ElectricAssist },
];

export class MicromobilityService {
  public static async getMicromobilityData(microMobilityFilters: IMicromobilityFilters | undefined): Promise<IMicromobilityData | undefined> {
    try {
      if (
        microMobilityFilters &&
        !microMobilityFilters.showBikeStations &&
        !microMobilityFilters.showElectricBikesVoi &&
        !microMobilityFilters.showScootersVoi &&
        !microMobilityFilters.showScootersRyde &&
        !microMobilityFilters.showScootersDott &&
        !microMobilityFilters.showElectricBikesDott
      ) {
        return undefined;
      }
      const response = await fetch('/api/labs/micromobility');
      const micromobilityDataApiModel = (await response.json()) as MicromobilityDataApiModel;
      const micromobilityData = MicromobilityDataApiModel.mapToBusinessModel(micromobilityDataApiModel);

      if (microMobilityFilters) {
        micromobilityData.docklessVehicles = MicromobilityHelper.filterDocklessVehiclesByFilters(micromobilityData.docklessVehicles, microMobilityFilters);
        if (!microMobilityFilters.showBikeStations) micromobilityData.stations = [];
      }
      return micromobilityData;
    } catch (error) {
      LogService.logError(error, 'Failed to fetch micromobility data');
      return undefined;
    }
  }

  public static async getJourneys(
    originGid: string | undefined,
    originName: string | undefined,
    originLatitude: number | undefined,
    originLongitude: number | undefined,
    destinationGid: string | undefined,
    destinationName: string | undefined,
    destinationLatitude: number | undefined,
    destinationLongitude: number | undefined,
    dateTime: DateTime | undefined,
    dateTimeRelatesTo: DateTimeRelatesTo,
    paginationReference: string | undefined,
    _limit: number | undefined,
    transportModes: TransportMode[] | undefined,
    transportSubModes: TransportSubMode[] | undefined,
    onlyDirectConnections: boolean | undefined,
    viaGid: string | undefined,
    interchangeDurationInMinutes: number | undefined,
    nearbyStopAreas: boolean | undefined,
    otherTransportModes: TransportMode[] | undefined,
    bikeMaxDistanceInMeters: number | undefined,
    bikeSpeedInKmPerHour: number | undefined,
    useOccupancy: boolean | undefined,
    microMobilityFilters: IMicromobilityFilters | undefined
  ): Promise<IPaginationResult<IJourney>> {
    // The algorithm is described here: https://vasttrafik.atlassian.net/wiki/spaces/NV/pages/362348861/LABS+Mikromobilitetsreseplanerare
    // 1. Get journeys from APR
    const result = await ApiPlaneraResaService.getJourneys(
      originGid,
      originName,
      originLatitude,
      originLongitude,
      destinationGid,
      destinationName,
      destinationLatitude,
      destinationLongitude,
      dateTime,
      dateTimeRelatesTo,
      paginationReference,
      MAX_NUMBER_OF_JOURNEYS,
      transportModes,
      transportSubModes,
      onlyDirectConnections,
      viaGid,
      interchangeDurationInMinutes,
      nearbyStopAreas,
      otherTransportModes,
      bikeMaxDistanceInMeters,
      bikeSpeedInKmPerHour,
      useOccupancy
    );
    // The limit parameter in APR is not working as expected, so we need to limit the number of journeys here
    result.results = result.results.slice(0, MAX_NUMBER_OF_JOURNEYS);

    // 2. Get coordinates for all micromobility vehicles
    const micromobilityData = await MicromobilityService.getMicromobilityData(microMobilityFilters);
    if (!micromobilityData) return result;

    // 3. For each journey
    const availableVehiclesCache: Record<string, VehicleWithDistance[]> = {};
    const availableStationsCache: Record<string, StationWithDistance[]> = {};
    for (const journey of clone(result.results)) {
      if (!journey.tripLegs || journey.tripLegs.length === 0) continue;

      let firstLegResults: IJourney[] = [];
      let lastLegResults: IJourney[] = [];
      if (journey.tripLegs.length > 1) {
        // NV-7137: Don't create micromobility journeys that replace the first leg if any of the other legs is cancelled
        const allTripLegsExceptTheFirst = journey.tripLegs.slice(1);
        if (!MicromobilityHelper.anyTripLegIsCancelledOrPartCancelled(allTripLegsExceptTheFirst)) {
          firstLegResults = await MicromobilityService.getMicromobilityJourneysReplacingFirstLeg(
            journey,
            micromobilityData,
            availableVehiclesCache,
            availableStationsCache,
            originGid,
            originName,
            originLatitude,
            originLongitude,
            destinationName
          );
        }

        // NV-7137: Don't create micromobility journeys that replace the last leg if any of the other legs is cancelled
        const allTripLegsExceptTheLast = journey.tripLegs.slice(0, -1);
        if (!MicromobilityHelper.anyTripLegIsCancelledOrPartCancelled(allTripLegsExceptTheLast)) {
          lastLegResults = await MicromobilityService.getMicromobilityJourneysReplacingLastLeg(
            journey,
            micromobilityData,
            availableVehiclesCache,
            availableStationsCache,
            originGid,
            originName,
            originLatitude,
            originLongitude,
            destinationGid,
            destinationName,
            destinationLatitude,
            destinationLongitude
          );
        }
      }

      const newJourneyAlternatives = [...firstLegResults, ...lastLegResults];
      newJourneyAlternatives.sort((a, b) => JourneyHelper.getDurationInMinutes(a, true) - JourneyHelper.getDurationInMinutes(b, true));
      const indexOfJourney = result.results.indexOf(journey);
      result.results.splice(indexOfJourney + 1, 0, ...newJourneyAlternatives);
    }

    // 4. Get micromobility journeys for the whole journey
    const wholeJourneyResults = await MicromobilityService.getMicromobilityJourneysForWholeJourney(
      micromobilityData,
      availableVehiclesCache,
      availableStationsCache,
      originGid,
      originName,
      originLatitude,
      originLongitude,
      destinationGid,
      destinationName,
      destinationLatitude,
      destinationLongitude,
      dateTimeRelatesTo,
      dateTime
    );

    if (wholeJourneyResults.length > 0) {
      for (const journey of wholeJourneyResults) {
        const journeyArrivalTime = JourneyHelper.getArrivalTimeForJourney(journey, true);
        const index = result.results.findIndex((j) => JourneyHelper.getArrivalTimeForJourney(j, true) > journeyArrivalTime);
        if (index === -1) {
          result.results.push(journey);
        } else {
          result.results.splice(index, 0, journey);
        }
      }
    }

    return result;
  }

  private static async getMicromobilityJourneysForWholeJourney(
    micromobilityData: IMicromobilityData,
    availableVehiclesCache: Record<string, VehicleWithDistance[]>,
    availableStationsCache: Record<string, StationWithDistance[]>,
    originGid: string | undefined,
    originName: string | undefined,
    originLatitude: number | undefined,
    originLongitude: number | undefined,
    destinationGid: string | undefined,
    destinationName: string | undefined,
    destinationLatitude: number | undefined,
    destinationLongitude: number | undefined,
    dateTimeRelatesTo: DateTimeRelatesTo = DateTimeRelatesTo.departure,
    dateTime: DateTime | undefined = DateTime.now()
  ): Promise<IJourney[]> {
    if (!originLatitude || !originLongitude || !destinationLatitude || !destinationLongitude) return [];
    const availableVehiclesAtOrigin = MicromobilityService.getAvailableVehicles(micromobilityData, availableVehiclesCache, { latitude: originLatitude, longitude: originLongitude });

    const result: IJourney[] = [];

    // === DOCKLESS VEHICLES ===
    for (const vehicleType of DOCKLESS_VEHICLE_TYPES) {
      const availableVehiclesOfSpecificTypeAtOrigin = availableVehiclesAtOrigin.filter(
        (x) => x.vehicle.formFactor === vehicleType.formFactor && x.vehicle.propulsionType === vehicleType.propulsionType
      );
      if (availableVehiclesOfSpecificTypeAtOrigin.length === 0) continue;
      const nearestVehicle = availableVehiclesOfSpecificTypeAtOrigin[0].vehicle;

      const walkJourney = await MicromobilityService.getWalkJourney(
        originGid,
        originName,
        originLatitude,
        originLongitude,
        undefined,
        nearestVehicle.id,
        nearestVehicle.latitude,
        nearestVehicle.longitude,
        dateTime,
        dateTimeRelatesTo
      );
      if (!walkJourney || !walkJourney.destinationLink) continue;
      const accessLink = walkJourney?.destinationLink ? MicromobilityHelper.createAccessLinkFromDestinationLink(walkJourney.destinationLink) : undefined;

      const bikeJourney = await MicromobilityService.getBikeJourney(
        nearestVehicle.latitude,
        nearestVehicle.longitude,
        destinationLatitude,
        destinationLongitude,
        accessLink ? DateTime.fromISO(accessLink?.destination.estimatedTime ?? accessLink?.destination.plannedTime) : dateTime,
        DateTimeRelatesTo.departure
      );
      if (!bikeJourney || !bikeJourney.destinationLink) continue;

      if (bikeJourney.destinationLink.distanceInMeters && bikeJourney.destinationLink.distanceInMeters > MAX_MICROMOBILITY_TRIP_LEG_LENGTH_IN_METERS) continue;

      const createdMicromobilityLink = MicromobilityHelper.createMicromobilityLinkForDocklessVehicle(
        nearestVehicle,
        availableVehiclesOfSpecificTypeAtOrigin.map((x) => x.vehicle),
        bikeJourney.destinationLink,
        StopAreaHelper.getNameWithoutMunicipality(originName) ?? '',
        StopAreaHelper.getNameWithoutMunicipality(destinationName) ?? ''
      );
      if (!createdMicromobilityLink) continue;

      result.push({
        id: bikeJourney.id,
        departureAccessLink: accessLink,
        startingMicromobilityLink: createdMicromobilityLink,
        tripLegs: [],
        connectionLinks: [],
        allLegs: [],
        detailsReference: bikeJourney.detailsReference,
      } as IJourney);
    }

    // === STATION-BOUND VEHICLES ===
    {
      const availableStationsAtOrigin = MicromobilityService.getAvailableStations(micromobilityData, availableStationsCache, { latitude: originLatitude, longitude: originLongitude });
      if (availableStationsAtOrigin.length === 0) return result;
      const availableStationsAtDestination = MicromobilityService.getAvailableStations(micromobilityData, availableStationsCache, {
        latitude: destinationLatitude,
        longitude: destinationLongitude,
      });
      if (availableStationsAtDestination.length === 0) return result;
      const nearestStationAtOrigin = availableStationsAtOrigin[0].station;
      const nearestStationAtDestination = availableStationsAtDestination[0].station;

      const departureWalkJourney = await MicromobilityService.getWalkJourney(
        originGid,
        originName,
        originLatitude,
        originLongitude,
        undefined,
        nearestStationAtOrigin.name,
        nearestStationAtOrigin.latitude,
        nearestStationAtOrigin.longitude,
        dateTime,
        dateTimeRelatesTo
      );
      if (!departureWalkJourney || !departureWalkJourney.destinationLink) return result;
      const departureAccessLink = departureWalkJourney?.destinationLink ? MicromobilityHelper.createAccessLinkFromDestinationLink(departureWalkJourney.destinationLink) : undefined;

      const bikeJourneyBetweenStations = await MicromobilityService.getBikeJourney(
        nearestStationAtOrigin.latitude,
        nearestStationAtOrigin.longitude,
        nearestStationAtDestination.latitude,
        nearestStationAtDestination.longitude,
        departureAccessLink ? DateTime.fromISO(departureAccessLink?.destination.plannedTime) : dateTime,
        dateTimeRelatesTo
      );
      if (!bikeJourneyBetweenStations || !bikeJourneyBetweenStations.destinationLink || !bikeJourneyBetweenStations.destinationLink.plannedArrivalTime) return result;

      if (bikeJourneyBetweenStations.destinationLink.distanceInMeters && bikeJourneyBetweenStations.destinationLink.distanceInMeters > MAX_MICROMOBILITY_TRIP_LEG_LENGTH_IN_METERS) return result;

      const arrivalWalkJourney = await MicromobilityService.getWalkJourney(
        undefined,
        nearestStationAtDestination.name,
        nearestStationAtDestination.latitude,
        nearestStationAtDestination.longitude,
        destinationGid,
        destinationName,
        destinationLatitude,
        destinationLongitude,
        DateTime.fromISO(bikeJourneyBetweenStations.destinationLink.plannedArrivalTime),
        DateTimeRelatesTo.departure
      );
      if (!arrivalWalkJourney || !arrivalWalkJourney.destinationLink) return result;
      const arrivalAccessLink = arrivalWalkJourney?.destinationLink ? MicromobilityHelper.createAccessLinkFromDestinationLink(arrivalWalkJourney.destinationLink) : undefined;

      const createdMicromobilityLink = MicromobilityHelper.createMicromobilityLinkBetweenStations(
        nearestStationAtOrigin,
        nearestStationAtDestination,
        availableStationsAtOrigin.map((x) => x.station),
        bikeJourneyBetweenStations.destinationLink,
        StopAreaHelper.getNameWithoutMunicipality(originName) ?? '',
        StopAreaHelper.getNameWithoutMunicipality(destinationName) ?? ''
      );
      if (!createdMicromobilityLink) return result;

      result.push({
        id: bikeJourneyBetweenStations.id,
        departureAccessLink: departureAccessLink,
        startingMicromobilityLink: createdMicromobilityLink,
        arrivalAccessLink: arrivalAccessLink,
        tripLegs: [],
        connectionLinks: [],
        allLegs: [],
        detailsReference: bikeJourneyBetweenStations.detailsReference,
      } as IJourney);
    }

    return result;
  }

  private static async getMicromobilityJourneysReplacingFirstLeg(
    journey: IJourney,
    micromobilityData: IMicromobilityData,
    availableVehiclesCache: Record<string, VehicleWithDistance[]>,
    availableStationsCache: Record<string, StationWithDistance[]>,
    originGid: string | undefined,
    originName: string | undefined,
    originLatitude: number | undefined,
    originLongitude: number | undefined,
    destinationName: string | undefined
  ): Promise<IJourney[]> {
    const docklessVehicleAlternatives = await MicromobilityService.getAlternativeWithDocklessMicromobility(
      journey,
      micromobilityData,
      availableVehiclesCache,
      originName,
      destinationName,
      JourneyHelper.getFirstPublicTransportLeg,
      MicromobilityHelper.getJourneyOrigin,
      MicromobilityHelper.getDestinationOfStartingMicromobilityLink,
      MicromobilityHelper.calculateTimeParametersForFirstLegBikeJourneySearch,
      async (nearestVehicle, createdMicromobilityLink) =>
        MicromobilityService.getWalkJourney(
          originGid,
          originName,
          originLatitude,
          originLongitude,
          undefined,
          nearestVehicle.id,
          nearestVehicle.latitude,
          nearestVehicle.longitude,
          DateTime.fromISO(createdMicromobilityLink.plannedDepartureTime).minus({ minutes: INTERCHANGE_DURATION_IN_MINUTES }),
          DateTimeRelatesTo.arrival
        ),
      MicromobilityHelper.updateJourneyWithStartingMicromobilityLink
    );

    const stationBoundAlternatives = await MicromobilityService.getAlternativesWithStationBoundMicromobility(
      journey,
      micromobilityData,
      availableStationsCache,
      originName,
      destinationName,
      JourneyHelper.getFirstPublicTransportLeg,
      MicromobilityHelper.getJourneyOrigin,
      MicromobilityHelper.getDestinationOfStartingMicromobilityLink,
      MicromobilityHelper.calculateTimeParametersForFirstLegBikeJourneySearch,
      (stationAtOrigin, _stationAtDestination, createdMicromobilityLink) =>
        MicromobilityService.getWalkJourney(
          originGid,
          originName,
          originLatitude,
          originLongitude,
          undefined,
          stationAtOrigin.name,
          stationAtOrigin.latitude,
          stationAtOrigin.longitude,
          DateTime.fromISO(createdMicromobilityLink.plannedDepartureTime).minus({ minutes: INTERCHANGE_DURATION_IN_MINUTES }),
          DateTimeRelatesTo.arrival
        ),
      MicromobilityHelper.updateJourneyWithStartingMicromobilityLink
    );

    return [...docklessVehicleAlternatives, ...stationBoundAlternatives];
  }

  private static async getMicromobilityJourneysReplacingLastLeg(
    journey: IJourney,
    micromobilityData: IMicromobilityData,
    availableVehiclesCache: Record<string, VehicleWithDistance[]>,
    availableStationsCache: Record<string, StationWithDistance[]>,
    _originGid: string | undefined,
    originName: string | undefined,
    _originLatitude: number | undefined,
    _originLongitude: number | undefined,
    destinationGid: string | undefined,
    destinationName: string | undefined,
    destinationLatitude: number | undefined,
    destinationLongitude: number | undefined
  ): Promise<IJourney[]> {
    const docklessVehicleAlternatives = await MicromobilityService.getAlternativeWithDocklessMicromobility(
      journey,
      micromobilityData,
      availableVehiclesCache,
      originName,
      destinationName,
      JourneyHelper.getLastPublicTransportLeg,
      MicromobilityHelper.getOriginOfEndingMicromobilityLink,
      MicromobilityHelper.getJourneyDestination,
      MicromobilityHelper.calculateTimeParametersForLastLegBikeJourneySearch,
      async () => undefined,
      MicromobilityHelper.updateJourneyWithEndingMicromobilityLink
    );

    const stationBoundAlternatives = await MicromobilityService.getAlternativesWithStationBoundMicromobility(
      journey,
      micromobilityData,
      availableStationsCache,
      originName,
      destinationName,
      JourneyHelper.getLastPublicTransportLeg,
      MicromobilityHelper.getOriginOfEndingMicromobilityLink,
      MicromobilityHelper.getJourneyDestination,
      MicromobilityHelper.calculateTimeParametersForLastLegBikeJourneySearch,
      (_stationAtOrigin, stationAtDestination, createdMicromobilityLink) =>
        MicromobilityService.getWalkJourney(
          undefined,
          stationAtDestination.name,
          stationAtDestination.latitude,
          stationAtDestination.longitude,
          destinationGid,
          destinationName,
          destinationLatitude,
          destinationLongitude,
          DateTime.fromISO(createdMicromobilityLink.plannedArrivalTime).plus({ minutes: INTERCHANGE_DURATION_IN_MINUTES }),
          DateTimeRelatesTo.departure
        ),
      MicromobilityHelper.updateJourneyWithEndingMicromobilityLink
    );

    return [...docklessVehicleAlternatives, ...stationBoundAlternatives];
  }

  private static async getAlternativeWithDocklessMicromobility(
    journey: IJourney,
    micromobilityData: IMicromobilityData,
    availableVehiclesCache: Record<string, VehicleWithDistance[]>,
    originName: string | undefined,
    destinationName: string | undefined,
    getTripLegFunc: (journey: IJourney) => ITripLeg | undefined,
    getOriginOfMicromobilityLinkFunc: (journey: IJourney) => ICoordinate | undefined,
    getDestinationOfMicromobilityLinkFunc: (journey: IJourney) => ICoordinate | undefined,
    getBikeJourneySearchTimeParameters: (journey: IJourney, interchangeDurationInMinutes: number) => { dateTime: DateTime; dateTimeRelatesTo: DateTimeRelatesTo } | undefined,
    getAccessLinkWalkJourneyFunc: (vehicle: IVehicle, createdMicromobilityLink: IMicromobilityLink) => Promise<IJourney | undefined>,
    updateJourneyWithMicromobilityLinkFunc: (journey: IJourney, micromobilityLink: IMicromobilityLink, departureAccessLink: IAccessLink | undefined, legIndexToReplace: number) => void
  ): Promise<IJourney[]> {
    const result: IJourney[] = [];

    // 3.1. Check if the trip leg could be replaced by a micromobility trip
    const tripLeg = getTripLegFunc(journey);
    if (!tripLeg || !MicromobilityHelper.tripLegCouldBeReplacedByMicromobility(tripLeg, MAX_TRIP_LEG_DURATION_IN_MINUTES)) return result;

    // 3.2. Check micromobility vehicles in the vicinity of the micromobility link origin
    const originOfMicromobilityLink = getOriginOfMicromobilityLinkFunc(journey);
    if (!originOfMicromobilityLink) return result;
    const availableVehiclesAtOrigin = MicromobilityService.getAvailableVehicles(micromobilityData, availableVehiclesCache, originOfMicromobilityLink);

    // 3.3. Calculate the destination of the micromobility link
    const destinationOfMicromobilityLink = getDestinationOfMicromobilityLinkFunc(journey);
    if (!destinationOfMicromobilityLink) return result;

    // 3.4. Calculate time parameters for the bike journey search that simulates the micromobility link
    const timeParameters = getBikeJourneySearchTimeParameters(journey, INTERCHANGE_DURATION_IN_MINUTES);
    if (!timeParameters) return result;

    // 3.5. Iterate over all dockless vehicle types
    for (const vehicleType of DOCKLESS_VEHICLE_TYPES) {
      // 3.5.1. Check if any vehicles are available at the origin
      const availableVehiclesOfSpecificTypeAtOrigin = availableVehiclesAtOrigin.filter(
        (x) => x.vehicle.formFactor === vehicleType.formFactor && x.vehicle.propulsionType === vehicleType.propulsionType
      );
      if (availableVehiclesOfSpecificTypeAtOrigin.length === 0) continue;

      // 3.5.2. Search for a bike journey from the location of the micromobility vehicle to the destination
      const nearestVehicle = availableVehiclesOfSpecificTypeAtOrigin[0].vehicle;
      const bikeJourney = await MicromobilityService.getBikeJourney(
        nearestVehicle.latitude,
        nearestVehicle.longitude,
        destinationOfMicromobilityLink.latitude,
        destinationOfMicromobilityLink.longitude,
        timeParameters.dateTime,
        timeParameters.dateTimeRelatesTo
      );
      if (!bikeJourney || !bikeJourney.destinationLink) continue;

      // 3.5.3. Create a micromobility link based on the bike journey
      const createdMicromobilityLink = MicromobilityHelper.createMicromobilityLinkForDocklessVehicle(
        nearestVehicle,
        availableVehiclesOfSpecificTypeAtOrigin.map((x) => x.vehicle),
        bikeJourney.destinationLink,
        StopAreaHelper.getNameWithoutMunicipality(originName) ?? '',
        StopAreaHelper.getNameWithoutMunicipality(destinationName) ?? ''
      );
      if (!createdMicromobilityLink) continue;

      // 3.5.4. NV-7084: Create an access link to/from the micromobility vehicle
      const walkJourney = await getAccessLinkWalkJourneyFunc(nearestVehicle, createdMicromobilityLink);
      const accessLink = walkJourney?.destinationLink ? MicromobilityHelper.createAccessLinkFromDestinationLink(walkJourney.destinationLink) : undefined;

      // 3.5.5. Create a new journey with the created micromobility link instead of the public transport leg
      const clonedJourney = cloneDeep(journey);
      updateJourneyWithMicromobilityLinkFunc(clonedJourney, createdMicromobilityLink, accessLink, tripLeg.journeyLegIndex);

      // Check that the departure time has not passed
      const departureTime = DateTime.fromISO(JourneyHelper.getDepartureTimeForJourney(clonedJourney, true));
      if (!departureTime || !departureTime.isValid || departureTime < DateTime.now()) continue;

      result.push(clonedJourney);
    }

    return result;
  }

  private static async getAlternativesWithStationBoundMicromobility(
    journey: IJourney,
    micromobilityData: IMicromobilityData,
    availableStationsCache: Record<string, StationWithDistance[]>,
    originName: string | undefined,
    destinationName: string | undefined,
    getTripLegFunc: (journey: IJourney) => ITripLeg | undefined,
    getOriginOfMicromobilityLinkFunc: (journey: IJourney) => ICoordinate | undefined,
    getDestinationOfMicromobilityLinkFunc: (journey: IJourney) => ICoordinate | undefined,
    getBikeJourneySearchTimeParameters: (journey: IJourney, interchangeDurationInMinutes: number) => { dateTime: DateTime; dateTimeRelatesTo: DateTimeRelatesTo } | undefined,
    getAccessLinkWalkJourneyFunc: (stationAtOrigin: IStation, stationAtDestination: IStation, createdMicromobilityLink: IMicromobilityLink) => Promise<IJourney | undefined>,
    updateJourneyWithMicromobilityLinkFunc: (journey: IJourney, micromobilityLink: IMicromobilityLink, departureAccessLink: IAccessLink | undefined, legIndexToReplace: number) => void
  ): Promise<IJourney[]> {
    const result: IJourney[] = [];

    const tripLeg = getTripLegFunc(journey);
    if (!tripLeg || !MicromobilityHelper.tripLegCouldBeReplacedByMicromobility(tripLeg, MAX_TRIP_LEG_DURATION_IN_MINUTES)) return result;

    const originOfEndingMicromobilityLink = getOriginOfMicromobilityLinkFunc(journey);
    if (!originOfEndingMicromobilityLink) return result;

    const destinationOfMicromobilityLink = getDestinationOfMicromobilityLinkFunc(journey);
    if (!destinationOfMicromobilityLink) return result;

    const timeParameters = getBikeJourneySearchTimeParameters(journey, INTERCHANGE_DURATION_IN_MINUTES);
    if (!timeParameters) return result;

    const availableStationsAtOrigin = MicromobilityService.getAvailableStations(micromobilityData, availableStationsCache, originOfEndingMicromobilityLink);
    if (availableStationsAtOrigin.length === 0) return result;
    const availableStationsAtDestination = MicromobilityService.getAvailableStations(micromobilityData, availableStationsCache, destinationOfMicromobilityLink);
    if (availableStationsAtDestination.length === 0) return result;

    const nearestStationAtOrigin = availableStationsAtOrigin[0].station;
    const nearestStationAtDestination = availableStationsAtDestination[0].station;
    const bikeJourneyBetweenStations = await MicromobilityService.getBikeJourney(
      nearestStationAtOrigin.latitude,
      nearestStationAtOrigin.longitude,
      nearestStationAtDestination.latitude,
      nearestStationAtDestination.longitude,
      timeParameters.dateTime,
      timeParameters.dateTimeRelatesTo
    );
    if (!bikeJourneyBetweenStations || !bikeJourneyBetweenStations.destinationLink) return result;

    const createdMicromobilityLink = MicromobilityHelper.createMicromobilityLinkBetweenStations(
      nearestStationAtOrigin,
      nearestStationAtDestination,
      availableStationsAtOrigin.map((x) => x.station),
      bikeJourneyBetweenStations.destinationLink,
      StopAreaHelper.getNameWithoutMunicipality(originName) ?? '',
      StopAreaHelper.getNameWithoutMunicipality(destinationName) ?? ''
    );
    if (!createdMicromobilityLink) return result;

    // 3.5.4. NV-7084: Create an access link to/from the station
    const walkJourney = await getAccessLinkWalkJourneyFunc(nearestStationAtOrigin, nearestStationAtDestination, createdMicromobilityLink);
    const accessLink = walkJourney?.destinationLink ? MicromobilityHelper.createAccessLinkFromDestinationLink(walkJourney.destinationLink) : undefined;

    // 3.7.3. Create a new journey with the created micromobility link instead of the first public transport leg
    const clonedJourney = cloneDeep(journey);
    updateJourneyWithMicromobilityLinkFunc(clonedJourney, createdMicromobilityLink, accessLink, tripLeg.journeyLegIndex);

    // Check that the departure time has not passed
    const departureTime = DateTime.fromISO(JourneyHelper.getDepartureTimeForJourney(clonedJourney, true));
    if (!departureTime || !departureTime.isValid || departureTime < DateTime.now()) return result;

    result.push(clonedJourney);
    return result;
  }

  private static getAvailableVehicles(micromobilityData: IMicromobilityData, availableVehiclesCache: Record<string, VehicleWithDistance[]>, coordinate: ICoordinate): VehicleWithDistance[] {
    const cacheKey = `${coordinate.latitude},${coordinate.longitude}`;
    let vehicles = availableVehiclesCache[cacheKey];
    if (!vehicles) {
      vehicles = MicromobilityHelper.getVehiclesWithinDistance(micromobilityData.docklessVehicles, coordinate.latitude, coordinate.longitude, MAX_WALKING_DISTANCE_IN_METERS);
      availableVehiclesCache[cacheKey] = vehicles;
    }
    return vehicles;
  }

  private static getAvailableStations(micromobilityData: IMicromobilityData, availableStationsCache: Record<string, StationWithDistance[]>, coordinate: ICoordinate): StationWithDistance[] {
    const cacheKey = `${coordinate.latitude},${coordinate.longitude}`;
    let stations = availableStationsCache[cacheKey];
    if (!stations) {
      stations = MicromobilityHelper.getStationsWithinDistance(micromobilityData.stations, coordinate.latitude, coordinate.longitude, MAX_WALKING_DISTANCE_IN_METERS).filter(
        (x) => x.station.availableVehicles > 0
      );
      availableStationsCache[cacheKey] = stations;
    }
    return stations;
  }

  private static async getBikeJourney(
    originLatitude: number,
    originLongitude: number,
    destinationLatitude: number,
    destinationLongitude: number,
    dateTime: DateTime,
    dateTimeRelatesTo: DateTimeRelatesTo
  ): Promise<IJourney | undefined> {
    try {
      const result = await ApiPlaneraResaService.getJourneys(
        undefined,
        'Micromobility vehicle position',
        originLatitude,
        originLongitude,
        undefined,
        'Journey destination',
        destinationLatitude,
        destinationLongitude,
        dateTime,
        dateTimeRelatesTo,
        undefined,
        1,
        [TransportMode.bike],
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        MAX_MICROMOBILITY_TRIP_LEG_LENGTH_IN_METERS,
        undefined,
        false
      );
      if (result.results.length === 0) return undefined;
      return result.results[0];
    } catch (error) {
      LogService.logError(error, 'Failed to fetch bike journey');
      return undefined;
    }
  }

  private static async getWalkJourney(
    originGid: string | undefined,
    originName: string | undefined,
    originLatitude: number | undefined,
    originLongitude: number | undefined,
    destinationGid: string | undefined,
    destinationName: string | undefined,
    destinationLatitude: number | undefined,
    destinationLongitude: number | undefined,
    dateTime: DateTime,
    dateTimeRelatesTo: DateTimeRelatesTo
  ): Promise<IJourney | undefined> {
    try {
      const result = await ApiPlaneraResaService.getJourneys(
        originGid,
        originName,
        originLatitude,
        originLongitude,
        destinationGid,
        destinationName,
        destinationLatitude,
        destinationLongitude,
        dateTime,
        dateTimeRelatesTo,
        undefined,
        1,
        [TransportMode.walk],
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        false
      );

      if (result.results.length === 0) return undefined;
      return result.results[0];
    } catch (error) {
      LogService.logError(error, 'Failed to fetch walk journey');
      return undefined;
    }
  }
}

